import React, { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { RegulationContext } from "../../App";
import { Months, startYear } from "../../core/constants/common";
import Utils from "../../core/utilities/Utils";
import {
	getCompanyInfoFromStorage,
	getMaxSelectableYear,
	getMaxYearlyPeriodEndYear,
	getYearDropdownLabel,
	isFinancialYearReporting,
} from "../../core/utilities/assessmentPeriodUtils";

const ReportingPeriod = ({ t, defaultPeriod, defaultRegulation, defaultFrequency, disabled = false, valueChangeCallback, maxYear, useFyYearLabels = false }) => {
	const defaultYear = defaultPeriod ? defaultPeriod.split('-')[0] : null;
	const defaultMonth = defaultPeriod ? defaultPeriod.split('-')[1] : null;
	const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
	const [frequency, setFrequency] = useState(defaultFrequency ?? "");
	const [frequencymonth, setFrequencyMonth] = useState(defaultPeriod ? defaultMonth : '04');
	const [annualyear, setAnnualYear] = useState(defaultPeriod ? defaultYear : new Date().getFullYear());
	const optionsMonth = Months({ t });
	const currentYear = new Date().getFullYear();

	const regulationId = defaultRegulation ? defaultRegulation.id : (glbRegulation?.id ?? '');
	const currentMonth = new Date().getMonth() + 1;
	useEffect(() => {
		if (regulationId) {
			let frequencyToUse;
			if (defaultFrequency) {
				frequencyToUse = defaultFrequency;
			} else {
				frequencyToUse = defaultRegulation ? defaultRegulation.frequency : glbRegulation.frequency;
			}
			const month = defaultPeriod
				? defaultMonth
				: Utils.getMonthFromFrequency(frequencyToUse);
			const defaultYear = defaultPeriod ? defaultPeriod.split('-')[0] : null;
			const year = defaultPeriod ? defaultYear : new Date().getFullYear();
			setFrequency(frequencyToUse);
			setFrequencyMonth(month);
			setAnnualYear(year);
		}
	}, [regulationId, defaultPeriod, defaultFrequency]);

	const buildYearSelectOptions = (freq) => {
		if (useFyYearLabels && freq === 'ANNUAL') {
			const companyInfo = getCompanyInfoFromStorage();
			const isFinancialYear = isFinancialYearReporting(companyInfo);
			const maxEndYear = isFinancialYear
				? getMaxYearlyPeriodEndYear(companyInfo)
				: getMaxSelectableYear();
			const options = [];
			for (let i = maxEndYear; i >= startYear; i--) {
				options.push(
					<option value={i} key={i}>{getYearDropdownLabel(i, isFinancialYear)}</option>
				);
			}
			return options;
		}
		const endYear = maxYear ?? currentYear;
		const options = [];
		for (let i = endYear + 1; i >= startYear; i--) {
			options.push(
				<option value={i} key={i}>{i}</option>
			);
		}
		return options;
	};

	const getReportingPeriodFieldToRender = () => {
		switch (frequency) {
			case 'ANNUAL':
				return (
					<select className="form-select"
						onChange={(e) => {
							setAnnualYear(e.target.value);
							valueChangeCallback({
								frequency: frequency,
								year: e.target.value,
								month: frequencymonth
							});
						}}
						value={annualyear}
						disabled={disabled}>
						{buildYearSelectOptions('ANNUAL')}
					</select>
				);
			case 'FINANCIAL_YEAR':
				return (<>
					<select className="form-select"
						onChange={(e) => {
							setAnnualYear(e.target.value);
							setFrequencyMonth('04');
							valueChangeCallback({
								frequency: frequency,
								year: e.target.value,
								month: '04'
							});
						}}
						value={annualyear}
						placeholderText="APRIL_MARCH"
						disabled={disabled}>
						{buildYearSelectOptions('FINANCIAL_YEAR')}
					</select>
					<span><strong>FY April-March</strong></span>
				</>);
			case 'QUARTERLY': {
				let quarterToSelect = '01';
				if (+frequencymonth >= 1 && +frequencymonth <= 3) {
					quarterToSelect = '01';
				} else if (+frequencymonth >= 4 && +frequencymonth <= 6) {
					quarterToSelect = '04';
				} else if (+frequencymonth >= 7 && +frequencymonth <= 9) {
					quarterToSelect = '07';
				} else {
					quarterToSelect = '10';
				}
				return (
					<div className="row m-0">
						<div className="col-lg-6 pe-2 ps-0">
							<select className="form-select"
								onChange={(e) => {
									setAnnualYear(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: e.target.value,
										month: frequencymonth
									});
								}}
								value={annualyear}
								disabled={disabled}>
								{buildYearSelectOptions('QUARTERLY')}
							</select>
						</div>

						<div className="col-lg-6 pe-2 ps-0">
							<select
								className="form-select"
								required
								value={quarterToSelect}
								onChange={(e) => {
									setFrequencyMonth(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: annualyear,
										month: e.target.value
									});
								}}
								disabled={disabled}>
								<option value="01">Q1</option>
								<option value="04">Q2</option>
								<option value="07">Q3</option>
								<option value="10">Q4</option>
							</select>
						</div>

					</div>);
			}
			case 'HALF_YEARLY': {
				let halfToSelect = '01';
				if (+frequencymonth >= 1 && +frequencymonth <= 6) {
					halfToSelect = '01';
				} else {
					halfToSelect = '07';
				}
				return (
					<div className="row ReportingPeriod-row">
						<select className="form-select"
							onChange={(e) => {
								setAnnualYear(e.target.value);
								valueChangeCallback({
									frequency: frequency,
									year: e.target.value,
									month: frequencymonth
								});
							}}
							value={annualyear}
							disabled={disabled}>
							{buildYearSelectOptions('HALF_YEARLY')}
						</select>
						<select
							className="form-select col-md-6"
							required
							value={halfToSelect}
							onChange={(e) => {
								setFrequencyMonth(e.target.value);
								valueChangeCallback({
									frequency: frequency,
									year: annualyear,
									month: e.target.value
								});
							}}
							disabled={disabled}>
							<option value="01">JAN-JUN</option>
							<option value="07">JUL-DEC</option>
						</select>
					</div>);
			}
			case 'MONTHLY':
				return (
					<div className="row ReportingPeriod-row">
						<div className="col-lg-6 col-md-6">
							<select className="form-select"
								onChange={(e) => {
									setAnnualYear(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: e.target.value,
										month: frequencymonth
									});
								}}
								value={annualyear}
								disabled={disabled}>
								{buildYearSelectOptions('MONTHLY')}
							</select>
						</div>
						<div className="col-lg-6 col-md-6">
							<select
								className="form-select"
								required
								value={frequencymonth}
								onChange={(e) => {
									setFrequencyMonth(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: annualyear,
										month: e.target.value
									});
								}}
								disabled={disabled}>
								{
									optionsMonth?.map((items) => (
										<option value={items.value} key={items.value}
											disabled={defaultRegulation && annualyear == currentYear && items.value > currentMonth ? true : false}>


											{items.label}
										</option>
									))
								}
							</select>
						</div>
					</div>);
			case 'CUSTOM':
				return (
					<div className="row ReportingPeriod-row">
						<div className="col-lg-6 col-md-6">
							<select className="form-select"
								onChange={(e) => {
									setAnnualYear(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: e.target.value,
										month: frequencymonth
									});
								}}
								value={annualyear}
								disabled={disabled}>
								{buildYearSelectOptions('CUSTOM')}
							</select>
						</div>
						<div className="col-lg-6 col-md-6">
							<select
								className="form-select"
								required
								value={frequencymonth}
								onChange={(e) => {
									setFrequencyMonth(e.target.value);
									valueChangeCallback({
										frequency: frequency,
										year: annualyear,
										month: e.target.value
									});
								}}
								disabled={disabled}>
								{
									optionsMonth?.map((items) => (
										<option value={items.value} key={items.value}
											disabled={defaultRegulation && annualyear == currentYear && items.value > currentMonth ? true : false}>


											{items.label}
										</option>
									))
								}
							</select>
						</div>
					</div>);
			default:
				return '';
		}
	};

	return (
		<div>
			{getReportingPeriodFieldToRender()}
		</div>
	);
};

export default withTranslation()(ReportingPeriod);